import { LicenseVariables } from "@/types/licensing";

export function getLicenseTextTemplate(
  licenseVariables: LicenseVariables,
  permissions: string[] = [],
  restrictions: string[] = [],
  language: 'en' | 'fr' = 'fr'
): string {
  const {
    rightsholder,
    mentions,
    hashtags,
    clip_language,
    license_validity_date,
    geographic_zone,
    authorized_platforms,
    max_extract_duration,
    native_monetization_allowed,
    ai_editing_tools_allowed,
    ai_visual_transformation_allowed,
    ai_training_database_allowed,
    challenge_participation
  } = licenseVariables;

  // Format the license validity date with custom text when no date is set
  let formattedValidityDate: string;
  if (license_validity_date) {
    formattedValidityDate = new Date(license_validity_date).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR');
  } else {
    if (language === 'en') {
      formattedValidityDate = 'This license is valid as long as the relevant Extract remains available on the ReCut platform, unless otherwise specified during download.\n\nIn case of withdrawal of the Extract by the rights holder or by ReCut, you will be informed, and you undertake not to republish or distribute your creation incorporating the Extract. Any publication prior to this withdrawal remains valid, subject to compliance with these terms.';
    } else {
      formattedValidityDate = 'Cette licence est valable tant que l\'Extrait concerné reste disponible sur la plateforme ReCut, sauf indication contraire précisée lors du téléchargement.\n\nEn cas de retrait de l\'Extrait par l\'ayant droit ou par ReCut, vous serez informé, et vous vous engagez à ne plus republier ou diffuser votre création intégrant l\'Extrait. Toute publication antérieure à ce retrait reste valable, sous réserve du respect des présentes.';
    }
  }

  // Format platforms list
  const platformsList = authorized_platforms?.join(', ') || (language === 'en' ? 'All platforms' : 'Toutes plateformes');

  // Format mentions and hashtags
  const mentionsList = mentions?.map(m => `@${m}`).join(', ') || '';
  const hashtagsList = hashtags?.map(h => `#${h}`).join(', ') || '';

  // Format max duration
  const maxDurationText = max_extract_duration 
    ? (language === 'en' ? `${max_extract_duration} seconds` : `${max_extract_duration} secondes`)
    : (language === 'en' ? 'Not specified' : 'Non spécifiée');

  // CSS styles for proper bullet points and formatting with dark mode support
  const styles = `
    <style>
      .license-content {
        font-family: Arial, sans-serif;
        line-height: 1.6;
        color: hsl(var(--foreground));
      }
      .license-content ul {
        margin: 12px 0;
        padding-left: 20px;
        list-style-type: disc;
      }
      .license-content li {
        margin: 6px 0;
        display: list-item;
        list-style-type: disc;
        list-style-position: outside;
        color: hsl(var(--foreground));
      }
      .license-content ol {
        margin: 12px 0;
        padding-left: 20px;
        list-style-type: decimal;
      }
      .license-content ol li {
        list-style-type: decimal;
        color: hsl(var(--foreground));
      }
      .license-content h3, .license-content h4, .license-content h5 {
        margin-top: 20px;
        margin-bottom: 10px;
        color: hsl(var(--foreground));
      }
      .license-content p {  
        margin: 12px 0;
        color: hsl(var(--foreground));
      }
      .license-content strong {
        color: hsl(var(--foreground));
        font-weight: bold;
      }
      .conditions-summary {
        background-color: hsl(var(--muted));
        border: 2px solid hsl(var(--border));
        border-radius: 8px;
        padding: 20px;
        margin: 24px 0;
      }
      .conditions-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 16px;
        margin-top: 16px;
      }
      .condition-item {
        background: hsl(var(--card));
        padding: 12px;
        border-radius: 6px;
        border-left: 4px solid hsl(var(--primary));
      }
      .condition-label {
        font-weight: bold;
        color: hsl(var(--foreground));
        margin-bottom: 4px;
      }
      .condition-value {
        color: hsl(var(--muted-foreground));
      }
      .status-allowed {
        color: #059669;
        font-weight: bold;
      }
      .status-forbidden {
        color: #dc2626;
        font-weight: bold;
      }
    </style>
  `;

  if (language === 'en') {
    return `
${styles}
<div class="license-content">  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 1 – LICENSE FRAMEWORK AND GENERAL PRINCIPLES</h3>
  
  <p>This license authorizes you to use a protected video extract (the "Extract"), made available by a rights holder via the ReCut platform, to create original content (called "UGC", for User Generated Content), which you can publish on your own social networks.</p>
  
  <p>This license is:</p>
  <ul>
    <li><strong>personal:</strong> it cannot be transferred or shared;</li>
    <li><strong>non-exclusive:</strong> other creators can use the same extract;</li>
    <li><strong>limited:</strong> it only gives you the rights specified in this license, and nothing more.</li>
  </ul>
  
  <p><strong>📌 Important:</strong><br>
  This license does not automatically give you the right:</p>
  <ul>
    <li>to be compensated;</li>
    <li>to monetize your content;</li>
    <li>or to resell your video.</li>
  </ul>
  
  <p>These cases are governed by other rules, specified in the following articles or in a separate challenge regulation.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 2 – CONDITIONS OF USE BY EXTRACT</h3>
  
  <p>Each extract available on ReCut is governed by parameters defined by the rights holder.<br>
  These conditions are an integral part of this license: they specify what you are allowed to do with this extract, on which platforms, for how long, and within what limits.</p>
  
  <p><strong>📌 General information:</strong></p>
  <ul>
    <li><strong>Rights holder:</strong> ${rightsholder}</li>
    <li><strong>Extract language:</strong> ${clip_language === 'fr' ? 'French (VF)' : clip_language === 'en' ? 'English (VO)' : clip_language}</li>
  </ul>
  
  <p><strong>📌 Duration and territory:</strong></p>
  <ul>
    <li><strong>License validity:</strong> ${license_validity_date ? `Until ${formattedValidityDate}` : formattedValidityDate}<br>
    ${license_validity_date ? 'At the end of this period, ReCut or the rights holder reserves the right to ask you to remove or limit access to your creation incorporating the Extract concerned.' : ''}</li>
    <li><strong>Authorized geographical zone:</strong> ${geographic_zone}<br>
    This license is valid only for publications intended for an audience located in the authorized zone.<br>
    You undertake to orient your creation towards this territory, in coherence with the language, cultural codes and distribution channels used.<br>
    Any distribution manifestly aimed at another audience is prohibited without prior agreement.</li>
  </ul>
  
  <p><strong>📌 Platforms and formats:</strong></p>
  <ul>
    <li><strong>Authorized platforms:</strong> ${platformsList}</li>
    <li><strong>Maximum duration of extract usable in your content:</strong> ${maxDurationText}</li>
  </ul>
  
  <p><strong>📌 Mentions and publication obligations:</strong></p>
  <ul>
    <li><strong>Required mentions:</strong> ${mentionsList}</li>
    <li><strong>Required hashtags:</strong> ${hashtagsList}</li>
  </ul>
  
  <p><strong>📌 Special conditions:</strong></p>
  <ul>
    <li><strong>Native monetization via social platforms (TikTok Creator Fund, YouTube Partner Program...):</strong> ${native_monetization_allowed ? 'Authorized' : 'Prohibited'}</li>
    <li><strong>Use of artificial intelligence tool on your creation:</strong>
      <ul>
        <li>Assisted editing tool (editing, subtitling, retouching): ${ai_editing_tools_allowed ? 'Authorized' : 'Prohibited'}</li>
        <li>Visual or sound transformation generated by AI (e.g.: AI voice, animation, filters modifying the extract): ${ai_visual_transformation_allowed ? 'Authorized' : 'Prohibited'}</li>
        <li>Use of the Extract or your creation to train an AI model, constitute a database, or perform automated analysis (e.g.: facial recognition, algorithmic classification): ${ai_training_database_allowed ? 'Authorized' : 'Prohibited'}</li>
      </ul>
      → See article 9 dedicated to artificial intelligence for more details
    </li>
    <li><strong>Participation in an active challenge:</strong> ${challenge_participation ? 'Yes' : 'No'}<br>
    ${challenge_participation ? '→ If yes, you will need to accept a complementary regulation specific to the challenge concerned.' : ''}</li>
  </ul>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 3 – USAGE RIGHTS GRANTED ON THE EXTRACT (rights granted by the rights holder)</h3>
  
  <p>You are authorized to use the Extract in compliance with the parameters defined in article 2.<br>
  This authorization includes the following rights:</p>
  <ul>
    <li>Download the Extract from the ReCut platform (reproduction right);</li>
    <li>Create original content (UGC) by integrating your own elements (text, music, effects, voice, etc.) → (adaptation right);</li>
    <li>Publish and distribute your creation on ReCut and the listed platforms, during the period and in the authorized zones → (representation right);</li>
    <li>Share your creation privately, for non-commercial purposes, by message or direct link (for example via messaging, email, closed group), provided that the Extract is not transmitted separately from your creation → (authorized private communication).</li>
  </ul>
  
  <h4>Commercial exploitation: strictly regulated</h4>
  <p>You cannot commercially exploit your creation, except in the following cases:</p>
  
  <h5>1. Assignment or sale of your composite work</h5>
  <p>Only within the framework of a specific agreement signed with ReCut or with the rights holder concerned.</p>
  
  <h5>2. External sponsoring from your publication (e.g. Spark Ads)</h5>
  <p>You can authorize ReCut, the rights holder or a partner to sponsor your creation directly from your own account on a social platform, notably via tools like Spark Ads on TikTok.<br>
  In this case, an explicit mention of commercial nature (sponsored content type) may be required, in accordance with the transparency rules in force on the platform concerned and consumer law. These mentions will be specified in the contractual documents or the regulation applicable to the challenge.</p>
  
  <h5>3. Special case: native monetization</h5>
  <p>Native monetization on social platforms (e.g. TikTok Creator Fund, YouTube Partner Program) is not considered commercial exploitation within the meaning of this license, unless otherwise stated.<br>
  It is authorized only if this faculty is expressly provided for in the conditions specific to the extract used (article 2).<br>
  This monetization is strictly limited:</p>
  <ul>
    <li>to your own content,</li>
    <li>published from your personal account,</li>
    <li>in compliance with the conditions set by the platform concerned.</li>
  </ul>
  
  <h5>4. Affiliate or performance activations (e.g. CPA)</h5>
  <p>In certain cases, you may choose to associate with your content a link tracking specific actions (click, purchase, viewing), giving you the right to a commission.<br>
  This type of activation can only occur within the framework of a challenge or a specific agreement with ReCut. It may involve adding commercial mentions or visual partnership indicators, under the conditions specified in the applicable regulation. In no case does this functionality oblige you to publish.</p>
  
  <p>Apart from these cases, any commercial use is prohibited without the prior agreement of the rights holder.</p>
  
  <h5>5. Other possible uses</h5>
  <p>Any other use of your creation or the Extract for commercial purposes (including within the framework of a partnership with a brand, a sponsored campaign, or third-party distribution) must be the subject of a separate specific agreement, concluded with ReCut and/or the rights holder concerned.</p>
  
  <h4>Participation in a challenge</h4>
  <p>If the Extract you use is associated with an active challenge, you will need to accept a complementary regulation specifying the conditions of participation, selection and any rewards.<br>
  No reward is due outside the framework defined by this regulation.</p>
  
  <h4>Nature of the license</h4>
  <p>This license is:</p>
  <ul>
    <li>personal (reserved to you only),</li>
    <li>non-exclusive (the extract can be used by other creators),</li>
    <li>non-transferable and non-sublicensable (you cannot assign it),</li>
    <li>limited to the rights explicitly granted herein.</li>
  </ul>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 4 – PROHIBITED USES AND LICENSE LIMITS</h3>
  
  <p>This license grants you certain rights, but it also comes with strict limits.<br>
  You undertake not to use the Extract in the following cases:</p>
  
  <h4>🔹 1. Infringement of legal framework or third party rights</h4>
  <p>You cannot:</p>
  <ul>
    <li>Publish content that violates the law or the rights of others (copyright, right to image, privacy, etc.);</li>
    <li>Pass off the Extract or your creation as official communication from a brand, studio or distributor (for example: by imitating their graphic charter, their tone or by usurping their identity).</li>
  </ul>
  
  <h4>🔹 2. Alteration or misappropriation of the work</h4>
  <p>You cannot:</p>
  <ul>
    <li>Modify the Extract in a way that undermines the original intention of the work or the image of its authors or rights holders (e.g.: deepfake, diverted voice, denaturation of meaning);</li>
    <li>Publish content whose message, staging or context diverts or degrades the image of the work or undermines the reputation of its authors or rights holders, particularly through violent, discriminatory, defamatory or misleading use.</li>
  </ul>
  <p>👉 For more details on uses related to artificial intelligence, see article 8.</p>
  
  <h4>🔹 3. Unauthorized technical or automated uses</h4>
  <p>You cannot:</p>
  <ul>
    <li>Extract the Extract to publish it alone, redistribute it as an autonomous file, or integrate it into a separate project (website, application, platform, etc.);</li>
    <li>Use the Extract or your creation within the framework of an artificial intelligence system for training, automated generation, technical analysis or algorithmic indexing purposes.</li>
  </ul>
  <p>👉 For more details on uses related to artificial intelligence, see article 8.</p>
  
  <h4>🔹 4. Non-compliance with parameters defined in the license</h4>
  <p>You cannot use the Extract beyond the authorized duration, on an unplanned platform, without the mandatory mentions or by activating unauthorized monetization.</p>
  
  <h4>🔹 5. Prohibited creation of digital assets</h4>
  <p>You cannot create, issue or sell an NFT, token or any other digital asset based in whole or in part on the Extract, even integrated into your creation, except with the express agreement of the rights holder.</p>
  
  <p><strong>📌 In case of non-compliance</strong><br>
  In case of non-compliance with this license, ReCut or the rights holder concerned may:</p>
  <ul>
    <li>notify you of the immediate withdrawal of your creation on the platforms concerned;</li>
    <li>suspend or revoke all or part of the usage rights granted;</li>
    <li>limit or delete your access to certain extracts, challenges or platform features.</li>
  </ul>
  <p>These measures can be taken without notice and without compensation. In case of serious or repeated infringement, definitive exclusion may be decided.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 5 – ADDING MUSIC OR THIRD-PARTY ELEMENTS</h3>
  
  <p>You can enrich your creation with other elements than the Extract, such as:</p>
  <ul>
    <li>music,</li>
    <li>texts,</li>
    <li>visual or sound effects,</li>
    <li>images or extracts for which you have usage rights.</li>
  </ul>
  
  <p>You are solely responsible for the elements you integrate. It is your responsibility to ensure that their use is lawful and compliant with applicable rules, particularly regarding copyright and music licenses.</p>
  
  <h4>What music can you use?</h4>
  
  <p>You can integrate into your creation:</p>
  
  <p><strong>✅ Royalty-free music</strong></p>
  <p>Free music or under free license (for example Creative Commons), usable according to the conditions provided (name mention, non-commercial use, etc.).</p>
  
  <p><strong>✅ Music purchased under license</strong></p>
  <p>Certain music libraries (e.g. Epidemic Sound, Artlist) offer paid music that you can use if:</p>
  <ul>
    <li>you have subscribed to a license in your name,</li>
    <li>the license covers use on social networks,</li>
    <li>and the use remains compliant with the limits provided by the license (e.g. no assignment to a third party, no sponsored use without license extension).</li>
  </ul>
  
  <p>⚠️ Most of these licenses do not allow ReCut or a rights holder to reuse or promote your creation without additional authorization.</p>
  
  <p><strong>✅ Music offered by social platforms</strong></p>
  <p>Platforms like TikTok, Instagram or YouTube make music available via two types of libraries:</p>
  
  <p><strong>- Personal library:</strong></p>
  <p>Accessible from a standard user account, it often includes protected music. This music is usable only within the framework of a personal, non-sponsored publication, on the platform concerned.</p>
  <p>Their presence in the library does not mean that you can use them freely.</p>
  <p>You cannot:</p>
  <ul>
    <li>boost or sponsor content containing this music;</li>
    <li>sell or transfer your creation to ReCut or a rights holder;</li>
    <li>integrate it into a commercial performance system (e.g. CPA).</li>
  </ul>
  <p>These uses may be blocked by agreements in force with collective management societies (e.g. SACEM).</p>
  
  <p><strong>- Commercial library (e.g. TikTok Commercial Music Library):</strong></p>
  <p>Reserved for professional accounts or sponsored content, it contains music specifically authorized for advertising uses.<br>
  Its use is possible only for this type of publication and if you yourself have access to this library via your account.</p>
  
  <h4>Limits in case of protected music</h4>
  <p>By protected music, we mean any work subject to copyright or neighboring rights (performers, producers, etc.).</p>
  <p>If you add protected music to your creation, personal publication remains possible but certain forms of commercial valorization will not be authorized, notably:</p>
  <ul>
    <li>sponsored highlighting (e.g. Spark Ads),</li>
    <li>buyback or integration into a campaign,</li>
    <li>or participation in commercial performance systems (CPA).</li>
  </ul>
  
  <h4>ReCut and ongoing developments</h4>
  <p>ReCut is actively working to frame the addition of music in remixed content, via:</p>
  <ul>
    <li>partnerships with music rights holders,</li>
    <li>identification of musical content compatible with sponsored uses,</li>
    <li>alert or automated filtering systems in case of risk of blocking.</li>
  </ul>
  <p>These developments will be specified in the regulations or specific contracts for each campaign or challenge.</p>
  
  <h4>Responsibility reminder</h4>
  <p>ReCut cannot be held responsible:</p>
  <ul>
    <li>for the unauthorized addition of third-party elements to your creation,</li>
    <li>nor for the legal, technical or commercial consequences that may result, particularly in case of blocking or withdrawal on a third-party platform.</li>
  </ul>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 6 – RIGHT OF REUSE BY ReCut</h3>
  
  <p>ReCut claims no ownership rights over your creation.</p>
  
  <p>However, by publishing content created from a licensed extract on the Platform, you accept that ReCut may, free of charge and non-exclusively:</p>
  <ul>
    <li>display or integrate your creation in pages or editorial spaces of the ReCut platform (for example: home page, catalog, challenge results),</li>
    <li>present it for institutional communication purposes, in media related to ReCut (newsletter, professional presentation, demonstration during an event, press kit),</li>
    <li>reshare it on social networks or official ReCut media, only in a logic of valorizing the challenge or the creative ecosystem.</li>
  </ul>
  
  <p>No other exploitation will be made without your prior agreement.<br>
  In particular, ReCut cannot sell, transfer, monetize or modify your creation without a new specific agreement with you.</p>
  
  <p><strong>📌 Sponsored highlighting at the initiative of the rights holder</strong><br>
  In certain cases, ReCut or the rights holder at the origin of the extract used may propose to promote your creation in a sponsored format (for example via an advertising campaign on social networks).</p>
  
  <p>This distribution can only take place with your express agreement, formalized in a specific agreement with ReCut or the rights holder concerned.</p>
  
  <p>This valorization may give rise to remuneration or revenue sharing, according to the terms defined between the parties.</p>
  
  <p>This license does not authorize any sponsored or commercial exploitation by default. It only provides for the possibility of contact for this purpose.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 7 – OWNERSHIP OF RIGHTS ON THE CREATION</h3>
  
  <p>You remain fully owner of the rights on your creation, as a composite work within the meaning of article L.113-2 of the Intellectual Property Code.</p>
  
  <p>This license does not constitute either an assignment or a transfer of ownership of your work. You retain all your rights on the content you have created from the extract.</p>
  
  <p>However, any commercial exploitation of your creation — apart from cases expressly provided for in the license (for example native monetization on platforms, if authorized) — can only occur with the prior agreement of the rights holder concerned.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 8 – USE OF ARTIFICIAL INTELLIGENCE</h3>
  
  <p>The use of artificial intelligence technologies within the framework of this license is strictly regulated.<br>
  The parameters applicable to each Extract are specified in article 2.</p>
  
  <h4>🔹 1. Assisted editing tools</h4>
  <p>The use of artificial intelligence tools for technical editing purposes is authorized, unless otherwise indicated in article 2.</p>
  
  <p>The following are notably concerned (non-exhaustive list):</p>
  <ul>
    <li>automated editing,</li>
    <li>subtitling or translation,</li>
    <li>audio enhancement or noise removal,</li>
    <li>format or duration adjustments (e.g. vertical cropping, automatic cutting, brightness correction).</li>
  </ul>
  
  <p>These operations are permitted provided that they do not modify the meaning, content, or appearance of the scene, and do not undermine the editorial integrity of the Extract.</p>
  
  <h4>🔹 2. Generative transformations</h4>
  <p>Any use of artificial intelligence aimed at generating, transforming or significantly altering visual or sound elements of an Extract is prohibited, unless expressly stated otherwise in article 2.</p>
  
  <p>The following are notably targeted (indicative list):</p>
  <ul>
    <li>voice synthesis or voice imitation,</li>
    <li>animation or modification of settings, characters, objects or expressions,</li>
    <li>addition of generative filters or fictional elements modifying the appearance or meaning of the scene,</li>
    <li>use of "deepfake", "voice cloning", or "style transfer" type technologies.</li>
  </ul>
  
  <h4>🔹 3. Prohibited technical uses</h4>
  <p>The use of the Extract or your creation within the framework of automated processing or algorithmic analysis is prohibited, except with prior written agreement from ReCut or the rights holder concerned.</p>
  
  <p>This notably includes (non-exhaustive list):</p>
  <ul>
    <li>training, calibration or testing of artificial intelligence models,</li>
    <li>insertion into a database intended for algorithmic analysis, automated generation or content indexing,</li>
    <li>automated extraction of information or characteristics (e.g. text and data mining, facial recognition, emotional analysis, classification),</li>
    <li>use as input corpus, source of inspiration or prompt to automatically generate content (text, image, sound or video), including via creation tools assisted integrating generative functions.</li>
  </ul>
  
  <p>This prohibition does not apply to uses limited to classic editing functions, under the conditions provided in point 1 of this article.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 9 – CONTENT WITHDRAWAL AND MODERATION</h3>
  
  <p>ReCut is responsible for the quality and security of content distributed via its platform, in compliance with usage licenses agreed with rights holders.</p>
  
  <p>As such, ReCut reserves the right to withdraw, hide, limit or deactivate at any time content created from a licensed extract, if this content:</p>
  <ul>
    <li>violates the conditions of this license or the Terms of Use,</li>
    <li>contravenes third-party rights (notably copyright, right to image, trademarks),</li>
    <li>undermines the image of the work or the rights holder who provided the extract,</li>
    <li>contains words or elements deemed inappropriate, controversial, hateful, violent, defamatory or illegal,</li>
    <li>disproportionately harms the reputation of ReCut or its partners.</li>
  </ul>
  
  <p>The withdrawal of content may be notified to the user by any appropriate means (email, platform interface). This withdrawal right is exercised without obligation to justify and without compensation. In case of withdrawal, you retain ownership of your creation, but the license to use the extract concerned is considered terminated.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 10 – RESPONSIBILITY OF EACH PARTY</h3>
  
  <p>You are solely responsible for the content you create from the extract made available by ReCut. It is your responsibility to respect the conditions of this license as well as third-party rights (notably copyright, neighboring rights, image rights and music rights).</p>
  
  <p>ReCut acts as a creation facilitator. The platform makes available extracts provided by partner rights holders and offers a secure contractual framework for their creative reuse.</p>
  
  <p>ReCut cannot be held responsible:</p>
  <ul>
    <li>for content you publish or modify from a licensed extract,</li>
    <li>for the addition by you of unauthorized third-party elements (music, images, effects...),</li>
    <li>for withdrawal, blocking or demonetization decisions made by social platforms,</li>
    <li>or for the impossibility of valorizing, republishing or remunerating content due to your editorial choices.</li>
  </ul>
  
  <p>Access to the ReCut platform may be temporarily suspended for maintenance or in case of technical failure. ReCut cannot be held responsible for service unavailability in case of force majeure, network incident or user equipment failure.</p>
  
  <p>The rights holder remains solely responsible for the compliance of the extracts they make available. In case of doubt or dispute over an extract, ReCut may suspend access to the content concerned without delay.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 11 – APPLICABLE LAW AND JURISDICTION</h3>
  
  <p>This license is governed by French law.</p>
  
  <p>Any dispute relating to its interpretation, execution or validity falls under the exclusive jurisdiction of the Paris courts, except for imperative contrary provisions.</p>
</div>
    `;
  }

  // French version - COMPLETE text as provided by user
  return `
${styles}
<div class="license-content">  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 1 – CADRE DE LA LICENCE ET PRINCIPES GÉNÉRAUX</h3>
  
  <p>Cette licence vous autorise à utiliser un extrait vidéo protégé (l'« Extrait »), mis à disposition par un ayant droit via la plateforme ReCut, pour créer un contenu original (appelé « UGC », pour User Generated Content), que vous pouvez publier sur vos propres réseaux sociaux.</p>
  
  <p>Cette licence est :</p>
  <ul>
    <li><strong>personnelle :</strong> elle ne peut être ni transférée, ni partagée ;</li>
    <li><strong>non exclusive :</strong> d'autres créateurs peuvent utiliser le même extrait ;</li>
    <li><strong>limitée :</strong> elle ne vous donne que les droits précisés dans cette licence, et rien de plus.</li>
  </ul>
  
  <p><strong>📌 Important :</strong><br>
  Cette licence ne vous donne pas automatiquement le droit :</p>
  <ul>
    <li>d'être rémunéré ;</li>
    <li>de monétiser votre contenu ;</li>
    <li>ou de revendre votre vidéo.</li>
  </ul>
  
  <p>Ces cas sont encadrés par d'autres règles, précisées dans les articles suivants ou dans un règlement de challenge distinct.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 2 – CONDITIONS D'UTILISATION PAR EXTRAIT</h3>
  
  <p>Chaque extrait disponible sur ReCut est encadré par des paramètres définis par l'ayant droit.<br>
  Ces conditions font partie intégrante de la présente licence : elles précisent ce que vous avez le droit de faire avec cet extrait, sur quelles plateformes, pendant combien de temps, et dans quelles limites.</p>
  
  <p><strong>📌 Informations générales :</strong></p>
  <ul>
    <li><strong>Ayant droit :</strong> ${rightsholder}</li>
    <li><strong>Langue de l'extrait :</strong> ${clip_language === 'fr' ? 'Français (VF)' : clip_language === 'en' ? 'Anglais (VO)' : clip_language}</li>
  </ul>
  
  <p><strong>📌 Durée et territoire :</strong></p>
  <ul>
    <li><strong>Validité de la licence :</strong> ${license_validity_date ? `Jusqu'au ${formattedValidityDate}` : formattedValidityDate}<br>
    ${license_validity_date ? 'Au terme de cette période, ReCut ou l\'ayant droit se réservent le droit de vous demander de retirer ou de limiter l\'accès à votre création intégrant l\'Extrait concerné.' : ''}</li>
    <li><strong>Zone géographique autorisée :</strong> ${geographic_zone}<br>
    Cette licence est valable uniquement pour des publications destinées à un public situé dans la zone autorisée.<br>
    Vous vous engagez à orienter votre création vers ce territoire, en cohérence avec la langue, les codes culturels et les canaux de diffusion utilisés.<br>
    Toute diffusion manifestement tournée vers un autre public est interdite sans accord préalable.</li>
  </ul>
  
  <p><strong>📌 Plateformes et formats :</strong></p>
  <ul>
    <li><strong>Plateformes autorisées :</strong> ${platformsList}</li>
    <li><strong>Durée maximale d'extrait utilisable dans votre contenu :</strong> ${maxDurationText}</li>
  </ul>
  
  <p><strong>📌 Mentions et obligations de publication :</strong></p>
  <ul>
    <li><strong>Mentions obligatoires :</strong> ${mentionsList}</li>
    <li><strong>Hashtags requis :</strong> ${hashtagsList}</li>
  </ul>
  
  <p><strong>📌 Conditions particulières :</strong></p>
  <ul>
    <li><strong>Monétisation native via les plateformes sociales (TikTok Creator Fund, YouTube Partner Program…) :</strong> ${native_monetization_allowed ? 'autorisée' : 'interdite'}</li>
    <li><strong>Usage d'un outil d'intelligence artificielle sur votre création :</strong>
      <ul>
        <li>Outil d'édition assistée (montage, sous-titrage, retouche) : ${ai_editing_tools_allowed ? 'autorisé' : 'interdit'}</li>
        <li>Transformation visuelle ou sonore générée par IA (ex. : voix IA, animation, filtres modifiant l'extrait) : ${ai_visual_transformation_allowed ? 'autorisée' : 'interdite'}</li>
        <li>Utilisation de l'Extrait ou de votre création pour entraîner un modèle d'IA, constituer une base de données, ou effectuer une analyse automatisée (ex. : reconnaissance faciale, classification algorithmique) : ${ai_training_database_allowed ? 'autorisée' : 'interdite'}</li>
      </ul>
      → Voir l'article 9 dédié à l'intelligence artificielle pour plus de détails
    </li>
    <li><strong>Participation à un challenge actif :</strong> ${challenge_participation ? 'oui' : 'non'}<br>
    ${challenge_participation ? '→ Si oui, vous devrez accepter un règlement complémentaire propre au challenge concerné.' : ''}</li>
  </ul>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 3 – DROITS D'USAGE CONCÉDÉS SUR L'EXTRAIT (droits concédés par l'ayant droit)</h3>
  
  <p>Vous êtes autorisé à utiliser l'Extrait dans le respect des paramètres définis à l'article 2.<br>
  Cette autorisation inclut les droits suivants :</p>
  <ul>
    <li>Télécharger l'Extrait depuis la plateforme ReCut (droit de reproduction) ;</li>
    <li>Créer un contenu original (UGC) en y intégrant vos propres éléments (texte, musique, effets, voix, etc.) → (droit d'adaptation) ;</li>
    <li>Publier et diffuser votre création sur ReCut et les plateformes listées, pendant la période et dans les zones autorisées → (droit de représentation) ;</li>
    <li>Partager votre création de manière privée, à titre non commercial, par message ou lien direct (par exemple via messagerie, e-mail, groupe fermé), à condition que l'Extrait ne soit pas transmis séparément de votre création → (communication privée autorisée).</li>
  </ul>
  
  <h4>Exploitation commerciale : strictement encadrée</h4>
  <p>Vous ne pouvez pas exploiter commercialement votre création, sauf dans les cas suivants :</p>
  
  <h5>1. Cession ou vente de votre œuvre composite</h5>
  <p>Uniquement dans le cadre d'un accord spécifique signé avec ReCut ou avec l'ayant droit concerné.</p>
  
  <h5>2. Sponsoring externe à partir de votre publication (ex. Spark Ads)</h5>
  <p>Vous pouvez autoriser ReCut, l'ayant droit ou un partenaire à sponsoriser votre création directement depuis votre propre compte sur une plateforme sociale, notamment via des outils comme Spark Ads sur TikTok.<br>
  Dans ce cas, une mention explicite de nature commerciale (type contenu sponsorisé) pourra être exigée, conformément aux règles de transparence en vigueur sur la plateforme concernée et au droit de la consommation. Ces mentions seront précisées dans les documents contractuels ou le règlement applicable au challenge.</p>
  
  <h5>3. Cas particulier : monétisation native</h5>
  <p>La monétisation native sur les plateformes sociales (ex. TikTok Creator Fund, YouTube Partner Program) n'est pas considérée comme une exploitation commerciale au sens de la présente licence, sauf disposition contraire.<br>
  Elle est autorisée uniquement si cette faculté est expressément prévue dans les conditions propres à l'extrait utilisé (article 2).<br>
  Cette monétisation est strictement limitée :</p>
  <ul>
    <li>à vos propres contenus,</li>
    <li>publiés depuis votre compte personnel,</li>
    <li>dans le respect des conditions fixées par la plateforme concernée.</li>
  </ul>
  
  <h5>4. Activations affiliées ou à la performance (ex. CPA)</h5>
  <p>Dans certains cas, vous pourrez choisir d'associer à votre contenu un lien traquant des actions spécifiques (clic, achat, visionnage), vous donnant droit à une commission.<br>
  Ce type d'activation ne peut intervenir que dans le cadre d'un challenge ou d'un accord spécifique avec ReCut. Elle pourra impliquer l'ajout de mentions commerciales ou d'indicateurs visuels de partenariat, dans les conditions précisées au règlement applicable. En aucun cas, cette fonctionnalité ne vous oblige à publier.</p>
  
  <p>En dehors de ces cas, toute utilisation commerciale est interdite sans l'accord préalable de l'ayant droit.</p>
  
  <h5>5. Autres usages possibles</h5>
  <p>Toute autre utilisation de votre création ou de l'Extrait à des fins commerciales (y compris dans le cadre d'un partenariat avec une marque, d'une campagne sponsorisée, ou d'une distribution tierce) devra faire l'objet d'un accord spécifique distinct, conclu avec ReCut et/ou l'ayant droit concerné.</p>
  
  <h4>Participation à un challenge</h4>
  <p>Si l'Extrait que vous utilisez est associé à un challenge actif, vous devrez accepter un règlement complémentaire précisant les conditions de participation, de sélection et les éventuelles gratifications.<br>
  Aucune gratification n'est due en dehors du cadre défini par ce règlement.</p>
  
  <h4>Nature de la licence</h4>
  <p>Cette licence est :</p>
  <ul>
    <li>personnelle (réservée à vous uniquement),</li>
    <li>non exclusive (l'extrait peut être utilisé par d'autres créateurs),</li>
    <li>non transférable et non sous-licenciable (vous ne pouvez pas la céder),</li>
    <li>limitée aux droits explicitement accordés par les présentes.</li>
  </ul>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 4 – USAGES INTERDITS ET LIMITES DE LA LICENCE</h3>
  
  <p>La présente licence vous accorde certains droits, mais elle s'accompagne aussi de limites strictes.<br>
  Vous vous engagez à ne pas utiliser l'Extrait dans les cas suivants :</p>
  
  <h4>🔹 1. Atteinte au cadre légal ou aux droits des tiers</h4>
  <p>Vous ne pouvez pas :</p>
  <ul>
    <li>Publier un contenu qui enfreint la loi ou les droits d'autrui (droits d'auteur, droit à l'image, vie privée, etc.) ;</li>
    <li>Faire passer l'Extrait ou votre création pour une communication officielle émanant d'une marque, d'un studio ou d'un distributeur (par exemple : en imitant leur charte graphique, leur ton ou en usurpant leur identité).</li>
  </ul>
  
  <h4>🔹 2. Altération ou détournement de l'œuvre</h4>
  <p>Vous ne pouvez pas :</p>
  <ul>
    <li>Modifier l'Extrait de manière à porter atteinte à l'intention initiale de l'œuvre ou à l'image de ses auteurs ou ayants droit (ex. : deepfake, voix détournée, dénaturation du sens) ;</li>
    <li>Publier un contenu dont le message, la mise en scène ou le contexte détourne ou dégrade l'image de l'œuvre ou porte atteinte à la réputation de ses auteurs ou ayants droit, en particulier par un usage violent, discriminatoire, diffamatoire ou trompeur.</li>
  </ul>
  <p>👉 Pour plus de détails sur les usages liés à l'intelligence artificielle, voir l'article 8.</p>
  
  <h4>🔹 3. Usages techniques ou automatisés non autorisés</h4>
  <p>Vous ne pouvez pas :</p>
  <ul>
    <li>Extraire l'Extrait pour le publier seul, le redistribuer sous forme de fichier autonome, ou l'intégrer dans un projet distinct (site web, application, plateforme, etc.) ;</li>
    <li>Utiliser l'Extrait ou votre création dans le cadre d'un système d'intelligence artificielle à des fins d'entraînement, de génération automatisée, d'analyse technique ou d'indexation algorithmique.</li>
  </ul>
  <p>👉 Pour plus de détails sur les usages liés à l'intelligence artificielle, voir l'article 8.</p>
  
  <h4>🔹 4. Non-respect des paramètres définis dans la licence</h4>
  <p>Vous ne pouvez pas utiliser l'Extrait au-delà de la durée autorisée, sur une plateforme non prévue, sans les mentions obligatoires ou en activant une monétisation non autorisée.</p>
  
  <h4>🔹 5. Création d'actifs numériques interdite</h4>
  <p>Vous ne pouvez pas créer, émettre ou vendre un NFT, un token ou tout autre actif numérique reposant en tout ou partie sur l'Extrait, même intégré dans votre création, sauf accord exprès de l'ayant droit.</p>
  
  <p><strong>📌 En cas de non-respect</strong><br>
  En cas de non-respect de la présente licence, ReCut ou l'ayant droit concerné pourront :</p>
  <ul>
    <li>vous notifier le retrait immédiat de votre création sur les plateformes concernées ;</li>
    <li>suspendre ou révoquer tout ou partie des droits d'usage accordés ;</li>
    <li>limiter ou supprimer votre accès à certains extraits, challenges ou fonctionnalités de la plateforme.</li>
  </ul>
  <p>Ces mesures peuvent être prises sans préavis et sans compensation. En cas d'infraction grave ou répétée, une exclusion définitive pourra être décidée.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 5 – AJOUT DE MUSIQUE OU D'ÉLÉMENTS TIERS</h3>
  
  <p>Vous pouvez enrichir votre création avec d'autres éléments que l'Extrait, comme :</p>
  <ul>
    <li>de la musique,</li>
    <li>des textes,</li>
    <li>des effets visuels ou sonores,</li>
    <li>des images ou extraits dont vous avez le droit d'usage.</li>
  </ul>
  
  <p>Vous êtes seul responsable des éléments que vous intégrez. Il vous revient de vous assurer que leur usage est licite et conforme aux règles applicables, notamment en matière de droits d'auteur et de licences musicales.</p>
  
  <h4>Quelles musiques pouvez-vous utiliser ?</h4>
  
  <p>Vous pouvez intégrer à votre création :</p>
  
  <p><strong>✅ Musiques libres de droits</strong></p>
  <p>Des musiques gratuites ou sous licence libre (par exemple Creative Commons), utilisables selon les conditions prévues (mention du nom, usage non commercial, etc.).</p>
  
  <p><strong>✅ Musiques achetées sous licence</strong></p>
  <p>Certaines bibliothèques musicales (ex. Epidemic Sound, Artlist) proposent des musiques payantes que vous pouvez utiliser si :</p>
  <ul>
    <li>vous avez souscrit une licence en votre nom,</li>
    <li>la licence couvre l'usage sur les réseaux sociaux,</li>
    <li>et l'utilisation reste conforme aux limites prévues par la licence (ex. pas de cession à un tiers, pas d'usage sponsorisé sans extension de licence).</li>
  </ul>
  
  <p>⚠️ La plupart de ces licences ne permettent pas à ReCut ou à un ayant droit de réutiliser ou promouvoir votre création sans autorisation complémentaire.</p>
  
  <p><strong>✅ Musiques proposées par les plateformes sociales</strong></p>
  <p>Les plateformes comme TikTok, Instagram ou YouTube mettent à disposition des musiques via deux types de bibliothèques :</p>
  
  <p><strong>- Bibliothèque personnelle :</strong></p>
  <p>Accessible depuis un compte utilisateur standard, elle inclut souvent des musiques protégées. Ces musiques sont utilisables uniquement dans le cadre d'une publication personnelle, non sponsorisée, sur la plateforme concernée.</p>
  <p>Leur présence dans la bibliothèque ne signifie pas que vous pouvez les utiliser librement.</p>
  <p>Vous ne pouvez pas :</p>
  <ul>
    <li>booster ou sponsoriser un contenu contenant cette musique ;</li>
    <li>vendre ou céder votre création à ReCut ou à un ayant droit ;</li>
    <li>l'intégrer à un dispositif de performance commerciale (ex. CPA).</li>
  </ul>
  <p>Ces usages peuvent être bloqués par les accords en vigueur avec les sociétés de gestion collective (ex. SACEM).</p>
  
  <p><strong>- Bibliothèque commerciale (ex. TikTok Commercial Music Library) :</strong></p>
  <p>Réservée aux comptes professionnels ou aux contenus sponsorisés, elle contient des musiques spécialement autorisées pour les usages publicitaires.<br>
  Son utilisation est possible uniquement pour ce genre de publication et si vous disposez vous-même d'un accès à cette bibliothèque via votre compte.</p>
  
  <h4>Limites en cas de musique protégée</h4>
  <p>Par musique protégée, on entend toute œuvre soumise à des droits d'auteur ou à des droits voisins (interprètes, producteurs, etc.).</p>
  <p>Si vous ajoutez une musique protégée à votre création, la publication personnelle reste possible mais certaines formes de valorisation commerciale ne seront pas autorisées, notamment :</p>
  <ul>
    <li>la mise en avant sponsorisée (ex. Spark Ads),</li>
    <li>le rachat ou l'intégration dans une campagne,</li>
    <li>ou la participation à des dispositifs de performance commerciale (CPA).</li>
  </ul>
  
  <h4>ReCut et les évolutions en cours</h4>
  <p>ReCut travaille activement à encadrer l'ajout de musique dans les contenus remixés, via :</p>
  <ul>
    <li>des partenariats avec ayants droit musicaux,</li>
    <li>l'identification de contenus musicaux compatibles avec des usages sponsorisés,</li>
    <li>des dispositifs d'alerte ou de filtrage automatisé en cas de risque de blocage.</li>
  </ul>
  <p>Ces évolutions seront précisées dans les règlements ou contrats spécifiques à chaque campagne ou challenge.</p>
  
  <h4>Rappel de responsabilité</h4>
  <p>ReCut ne saurait être tenu responsable :</p>
  <ul>
    <li>de l'ajout non autorisé d'éléments tiers à votre création,</li>
    <li>ni des conséquences juridiques, techniques ou commerciales pouvant en résulter, notamment en cas de blocage ou de retrait sur une plateforme tierce.</li>
  </ul>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 6 – DROIT DE REUTILISATION PAR ReCut</h3>
  
  <p>ReCut ne revendique aucun droit de propriété sur votre création.</p>
  
  <p>Toutefois, en publiant un contenu réalisé à partir d'un extrait sous licence sur la Plateforme, vous acceptez que ReCut puisse, à titre gratuit et non exclusif :</p>
  <ul>
    <li>afficher ou intégrer votre création dans des pages ou espaces éditoriaux de la plateforme ReCut (par exemple : page d'accueil, catalogue, résultats de challenge),</li>
    <li>la présenter à des fins de communication institutionnelle, dans des supports liés à ReCut (newsletter, présentation professionnelle, démonstration lors d'un événement, dossier de presse),</li>
    <li>la repartager sur les réseaux sociaux ou supports officiels de ReCut, uniquement dans une logique de valorisation du challenge ou de l'écosystème créatif.</li>
  </ul>
  
  <p>Aucune autre exploitation ne sera faite sans votre accord préalable.<br>
  En particulier, ReCut ne pourra pas vendre, céder, monétiser ou modifier votre création sans un nouvel accord spécifique avec vous.</p>
  
  <p><strong>📌 Mise en avant sponsorisée à l'initiative de l'ayant droit</strong><br>
  Dans certains cas, ReCut ou l'ayant droit à l'origine de l'extrait utilisé pourra proposer de promouvoir votre création dans un format sponsorisé (par exemple via une campagne publicitaire sur les réseaux sociaux).</p>
  
  <p>Cette diffusion ne pourra avoir lieu qu'avec votre accord exprès, formalisé dans un accord spécifique avec ReCut ou l'ayant droit concerné.</p>
  
  <p>Cette valorisation pourra donner lieu à une rémunération ou à un partage de revenus, selon les termes définis entre les parties.</p>
  
  <p>La présente licence n'autorise aucune exploitation sponsorisée ou commerciale par défaut. Elle prévoit uniquement la possibilité d'une prise de contact à cette fin.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 7 – TITULARITÉ DES DROITS SUR LA CRÉATION</h3>
  
  <p>Vous restez pleinement titulaire des droits sur votre création, en tant qu'œuvre composite au sens de l'article L.113-2 du Code de la propriété intellectuelle.</p>
  
  <p>La présente licence ne constitue ni une cession, ni un transfert de propriété de votre œuvre. Vous conservez l'ensemble de vos droits sur le contenu que vous avez réalisé à partir de l'extrait.</p>
  
  <p>En revanche, toute exploitation commerciale de votre création — en dehors des cas expressément prévus dans la licence (par exemple la monétisation native sur les plateformes, si elle est autorisée) — ne pourra intervenir qu'avec l'accord préalable de l'ayant droit concerné.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 8 – UTILISATION D'INTELLIGENCE ARTIFICIELLE</h3>
  
  <p>L'usage de technologies d'intelligence artificielle dans le cadre de la présente licence est strictement encadré.<br>
  Les paramètres applicables à chaque Extrait sont précisés à l'article 2.</p>
  
  <h4>🔹 1. Outils d'édition assistée</h4>
  <p>L'utilisation d'outils d'intelligence artificielle à des fins d'édition technique est autorisée, sauf indication contraire à l'article 2.</p>
  
  <p>Sont notamment concernés (liste non limitative) :</p>
  <ul>
    <li>le montage automatisé,</li>
    <li>le sous-titrage ou la traduction,</li>
    <li>l'amélioration audio ou la suppression de bruit,</li>
    <li>des ajustements de format ou de durée (ex. recadrage vertical, coupe automatique, correction de luminosité).</li>
  </ul>
  
  <p>Ces opérations sont permises à condition qu'elles ne modifient ni le sens, ni le contenu, ni l'apparence de la scène, et ne portent pas atteinte à l'intégrité éditoriale de l'Extrait.</p>
  
  <h4>🔹 2. Transformations génératives</h4>
  <p>Toute utilisation d'intelligence artificielle visant à générer, transformer ou altérer de manière significative des éléments visuels ou sonores d'un Extrait est interdite, sauf mention expresse contraire à l'article 2.</p>
  
  <p>Sont notamment visés (liste indicative) :</p>
  <ul>
    <li>la synthèse vocale ou l'imitation de voix,</li>
    <li>l'animation ou la modification de décors, personnages, objets ou expressions,</li>
    <li>l'ajout de filtres génératifs ou d'éléments fictifs modifiant l'apparence ou le sens de la scène,</li>
    <li>le recours à des technologies de type "deepfake", "voice cloning", ou "style transfer".</li>
  </ul>
  
  <h4>🔹 3. Usages techniques interdits</h4>
  <p>L'utilisation de l'Extrait ou de votre création dans le cadre de traitements automatisés ou d'analyses algorithmiques est interdite, sauf accord écrit préalable de ReCut ou de l'ayant droit concerné.</p>
  
  <p>Cela inclut notamment (liste non limitative) :</p>
  <ul>
    <li>l'entraînement, la calibration ou le test de modèles d'intelligence artificielle,</li>
    <li>l'insertion dans une base de données destinée à l'analyse algorithmique, à la génération automatisée ou à l'indexation de contenus,</li>
    <li>l'extraction automatisée d'informations ou de caractéristiques (ex. text and data mining, reconnaissance faciale, analyse émotionnelle, classification),</li>
    <li>l'utilisation comme corpus d'entrée, source d'inspiration ou prompt pour générer automatiquement des contenus (texte, image, son ou vidéo), y compris via des outils de création assistée intégrant des fonctions génératives.</li>
  </ul>
  
  <p>Cette interdiction ne s'applique pas aux usages limités à des fonctions d'édition classiques, dans les conditions prévues au point 1 du présent article.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 9 – RETRAIT DE CONTENU ET MODÉRATION</h3>
  
  <p>ReCut est responsable de la qualité et de la sécurité des contenus diffusés via sa plateforme, dans le respect des licences d'usage convenues avec les ayants droit.</p>
  
  <p>À ce titre, ReCut se réserve le droit de retirer, masquer, limiter ou désactiver à tout moment un contenu réalisé à partir d'un extrait sous licence, si ce contenu :</p>
  <ul>
    <li>enfreint les conditions de la présente licence ou des CGU,</li>
    <li>contrevient à des droits de tiers (notamment droit d'auteur, droit à l'image, marques),</li>
    <li>porte atteinte à l'image de l'œuvre ou de l'ayant droit ayant fourni l'extrait,</li>
    <li>contient des propos ou éléments jugés inappropriés, polémiques, haineux, violents, diffamatoires ou illicites,</li>
    <li>nuit de manière disproportionnée à la réputation de ReCut ou de ses partenaires.</li>
  </ul>
  
  <p>Le retrait d'un contenu peut faire l'objet d'une notification à l'utilisateur par tout moyen approprié (e-mail, interface de la plateforme). Ce droit de retrait s'exerce sans obligation de justification et sans compensation. En cas de retrait, vous conservez la propriété de votre création, mais la licence d'utilisation de l'extrait concerné est considérée comme résiliée.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 10 – RESPONSABILITÉ DE CHAQUE PARTIE</h3>
  
  <p>Vous êtes seul responsable du contenu que vous créez à partir de l'extrait mis à disposition par ReCut. Il vous appartient de respecter les conditions de la présente licence ainsi que les droits des tiers (notamment les droits d'auteur, droits voisins, droits à l'image et droits musicaux).</p>
  
  <p>ReCut agit en qualité de facilitateur de création. La plateforme met à disposition des extraits fournis par les ayants droit partenaires et propose un cadre contractuel sécurisé pour leur réutilisation créative.</p>
  
  <p>ReCut ne pourra être tenu responsable :</p>
  <ul>
    <li>des contenus que vous publiez ou modifiez à partir d'un extrait sous licence,</li>
    <li>de l'ajout par vos soins d'éléments tiers non autorisés (musique, images, effets…),</li>
    <li>des décisions de retrait, blocage ou démonétisation prises par des plateformes sociales,</li>
    <li>ou de l'impossibilité de valoriser, republier ou rémunérer un contenu du fait de vos choix éditoriaux.</li>
  </ul>
  
  <p>L'accès à la plateforme ReCut peut être temporairement suspendu pour maintenance ou en cas de défaillance technique. ReCut ne pourra être tenu responsable d'une indisponibilité du service en cas de force majeure, d'incident de réseau ou de défaillance de l'équipement utilisateur.</p>
  
  <p>L'ayant droit reste seul responsable de la conformité des extraits qu'il met à disposition. En cas de doute ou de litige sur un extrait, ReCut pourra suspendre l'accès au contenu concerné sans délai.</p>
  
  <h3 style="color: #2563eb; font-weight: bold; margin: 24px 0 12px 0;">🟦 ARTICLE 11 – DROIT APPLICABLE ET JURIDICTION</h3>
  
  <p>La présente licence est régie par le droit français.</p>
  
  <p>Tout litige relatif à son interprétation, son exécution ou sa validité relève de la compétence exclusive des juridictions de Paris, sauf disposition impérative contraire.</p>
  
</div>
  `;
}
